/**
 * /suppliers route — searchable supplier list page.
 *
 * Design decisions:
 * - validateSearch uses Zod inline schema with .optional().catch(undefined) on q
 * - Permission check is done in SuppliersListPage (canView gate) so that a user
 *   without supplier.view lands on /suppliers and sees a "permission denied" message
 *   rather than being silently redirected — this makes the denied state testable.
 * - component is a thin wrapper rendering SuppliersListPage
 */
import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { SuppliersListPage } from '../../features/suppliers/components/suppliers-list-page';
import { PageTitleContext } from '../__root';

// ─── Search schema ────────────────────────────────────────────

const SuppliersSearchSchema = z.object({
  q: z.string().optional().catch(undefined),
});

// ─── Route definition ─────────────────────────────────────────

export const Route = createFileRoute('/suppliers/')({
  validateSearch: SuppliersSearchSchema,
  // No beforeLoad redirect: permission check is handled in SuppliersListPage so that
  // users without supplier.view see a permission-denied message at /suppliers rather than
  // being silently redirected away.
  component: SuppliersPage,
});

// ─── Page component ───────────────────────────────────────────

function SuppliersPage() {
  const { setPageTitle } = React.useContext(PageTitleContext);
  const { t } = useTranslation('suppliers');

  React.useEffect(() => {
    setPageTitle(t('pageTitle'));
  }, [setPageTitle, t]);

  return <SuppliersListPage />;
}
